/** @jsxImportSource @emotion/react */
import { CSSProperties, useCallback, useMemo, useState } from "react";
import {
  Background,
  ReactFlow,
  Panel,
  Position,
  Node,
  Edge,
  NodeChange,
  XYPosition,
  Controls,
  MarkerType,
} from "@xyflow/react";
import dagre, { EdgeLabel, GraphLabel, NodeLabel } from "@dagrejs/dagre";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import "./workflow.css";
import { EuiButton, EuiFlexGroup } from "@inscopix/ideas-eui";
const position = { x: 0, y: 0 };
const edgeType = "smoothstep";

export const initialNodes: Node<{ label: string }>[] = [
  {
    id: "1",
    type: "input",
    data: { label: "input" },
    position,
  },
  {
    id: "2",
    data: { label: "node 2" },
    position,
  },
  {
    id: "2a",
    data: { label: "node 2a" },
    position,
  },
  {
    id: "2b",
    data: { label: "node 2b" },
    position,
  },
  {
    id: "2c",
    data: { label: "node 2c" },
    position,
  },
  {
    id: "2d",
    data: { label: "node 2d" },
    position,
  },
  {
    id: "3",
    data: { label: "node 3" },
    position,
  },
  {
    id: "4",
    data: { label: "node 4" },
    position,
  },
  {
    id: "5",
    data: { label: "node 5" },
    position,
  },
  {
    id: "6",
    type: "output",
    data: { label: "output" },
    position,
  },
  { id: "7", type: "output", data: { label: "output" }, position },
];

export const initialEdges = [
  { id: "e12", source: "1", target: "2", type: edgeType, animated: true },
  { id: "e13", source: "1", target: "3", type: edgeType, animated: true },
  { id: "e22a", source: "2", target: "2a", type: edgeType, animated: true },
  { id: "e22b", source: "2", target: "2b", type: edgeType, animated: true },
  { id: "e22c", source: "2", target: "2c", type: edgeType, animated: true },
  { id: "e2c2d", source: "2c", target: "2d", type: edgeType, animated: true },
  { id: "e45", source: "4", target: "5", type: edgeType, animated: true },
  { id: "e56", source: "5", target: "6", type: edgeType, animated: true },
  { id: "e57", source: "5", target: "7", type: edgeType, animated: true },
];

type LayoutDirection = "TB" | "LR";
type Size = { width: number; height: number };

const nodeWidth = 172;
const nodeHeight = 36;

const getLayoutedPositions = (
  nodes: Pick<Node, "id">[],
  edges: Pick<Edge, "source" | "target">[],
  direction: LayoutDirection,
): Record<string, XYPosition> => {
  const dagreGraph = new dagre.graphlib.Graph<
    GraphLabel,
    NodeLabel,
    EdgeLabel
  >().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const positions: Record<string, XYPosition> = {};
  nodes.forEach((node) => {
    const p = dagreGraph.node(node.id);
    positions[node.id] = {
      x: (p?.x ?? 0) - nodeWidth / 2,
      y: (p?.y ?? 0) - nodeHeight / 2,
    };
  });
  return positions;
};

type OutputPolicy = "KEEP" | "DISCARD";

type GraphNode<T extends Record<string, string>> = Pick<Node<T>, "id" | "data">;
export interface WorkflowDiagramReactFlowProps {
  graphNodes: GraphNode<{ label: string; color?: string }>[];
  graphInputs: GraphNode<{ label: string }>[];
  graphResults: GraphNode<{
    label: string;
    outputPolicy: OutputPolicy;
  }>[];
  graphEdges: Pick<Edge, "id" | "source" | "target">[];
}

const nodeColors = {
  tool: "#8db802",
  input: "#7ec8fc",
  result: "#e5f101",
};

const resultStyles: Record<OutputPolicy, CSSProperties> = {
  KEEP: {
    backgroundColor: nodeColors.result,
  },
  DISCARD: {
    backgroundColor: "#f7f8d6",
    border: "1px dashed #9ca3af",
    color: "#6b7280",
    opacity: 0.6,
  },
};

export const WorkflowDiagramReactFlow = ({
  graphNodes,
  graphInputs,
  graphResults,
  graphEdges,
}: WorkflowDiagramReactFlowProps) => {
  const [direction, setDirection] = useState<LayoutDirection>("TB");
  const [dragged, setDragged] = useState<Record<string, XYPosition>>({});
  const [measured, setMeasured] = useState<Record<string, Size>>({});

  const builtNodes = useMemo<Node[]>(
    () => [
      ...graphInputs.map((i) => ({
        ...i,
        style: { backgroundColor: nodeColors.input },
        position,
      })),
      ...graphNodes.map((n) => ({
        ...n,
        position,
        style: { backgroundColor: n.data.color ?? nodeColors.tool },
      })),
      ...graphResults.map((r) => ({
        ...r,
        position,
        style: resultStyles[r.data.outputPolicy ?? "KEEP"],
      })),
    ],
    [graphInputs, graphNodes, graphResults],
  );

  const edges = useMemo<Edge[]>(
    () =>
      graphEdges.map((e) => ({
        ...e,
        markerEnd: {
          type: MarkerType.Arrow,
          width: 10,
          height: 10,
          color: "#4c4646",
        },
        style: { strokeWidth: 1.5, stroke: "#4c4646" },
      })),
    [graphEdges],
  );

  const autoPositions = useMemo(
    () => getLayoutedPositions(builtNodes, edges, direction),
    [builtNodes, edges, direction],
  );

  const nodes = useMemo<Node[]>(() => {
    const isHorizontal = direction === "LR";
    return builtNodes.map((n) => ({
      ...n,
      measured: measured[n.id],
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: dragged[n.id] ?? autoPositions[n.id] ?? n.position,
    }));
  }, [builtNodes, dragged, measured, autoPositions, direction]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setDragged((prev) => {
      let next = prev;
      for (const change of changes) {
        if (change.type === "position" && change.position) {
          if (next === prev) next = { ...prev };
          next[change.id] = change.position;
        }
      }
      return next;
    });

    setMeasured((prev) => {
      let next = prev;
      for (const change of changes) {
        if (change.type === "dimensions" && change.dimensions) {
          if (next === prev) next = { ...prev };
          next[change.id] = change.dimensions;
        }
      }
      return next;
    });
  }, []);

  const onLayout = useCallback((nextDirection: LayoutDirection) => {
    setDragged({});
    setDirection(nextDirection);
  }, []);

  return (
    <ReactFlow
      style={{ height: "100%", width: "100%" }}
      nodes={nodes}
      edges={edges}
      nodesConnectable={false}
      onNodesChange={onNodesChange}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      colorMode="light"
    >
      <Controls />
      <Panel position="top-right">
        <EuiFlexGroup>
          <EuiButton onClick={() => onLayout("TB")}>vertical layout</EuiButton>
          <EuiButton onClick={() => onLayout("LR")}>
            horizontal layout
          </EuiButton>
        </EuiFlexGroup>
      </Panel>
      <Background />
    </ReactFlow>
  );
};
